import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Pill } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, money, num } from '../../lib/format';
import { PriceListEditor } from './PriceListEditor';

export type PriceListRow = {
  id: string;
  name: string;
  currency: string;
  valid_from?: string;
  valid_until?: string;
  active: number;
  notes?: string;
  legacy_erpnext_id?: string;
  line_count?: number;
  lines?: PriceListItem[];
};

export type PriceListItem = {
  id: string;
  item_id?: string;
  item_code?: string;
  item_name?: string;
  unit_price: number;
  min_quantity: number;
  currency?: string;
  uom?: string;
  customer_code?: string;
  valid_from?: string;
  valid_until?: string;
  item_description?: string;
  legacy_erpnext_id?: string;
};

function activePill(a: number) {
  return a ? <Pill tone="ok" label="启用" /> : <Pill tone="neutral" label="停用" />;
}

export function PriceListsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canCreate = can(user, 'price-lists', 'create');
  const canEdit = can(user, 'price-lists', 'edit');
  const [reloadSignal, setReloadSignal] = useState(0);
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; row: PriceListRow } | null>(null);
  const [delRow, setDelRow] = useState<PriceListRow | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  const remove = async () => {
    if (!delRow) return;
    setDelBusy(true);
    setDelErr(null);
    try {
      await api.del(`/api/price-lists/${delRow.id}`);
      setDelRow(null);
      refresh();
    } catch (e) {
      setDelErr(e instanceof ApiError ? e.message : '删除失败');
    } finally {
      setDelBusy(false);
    }
  };

  const columns: Column<PriceListRow>[] = [
    { header: '价目表', render: (r) => <span className="strong">{r.name}</span> },
    { header: '币种', render: (r) => <span className="num">{r.currency || '—'}</span> },
    { header: '有效期', render: (r) => (
      <span className="num">
        {r.valid_from ? dateLabel(r.valid_from) : '—'}
        {r.valid_until ? ` ~ ${dateLabel(r.valid_until)}` : ''}
      </span>
    )},
    { header: '价格条数', render: (r) => <span className="num">{num(r.line_count ?? (r.lines?.length ?? 0), 0)}</span> },
    { header: '状态', render: (r) => activePill(r.active) },
    { header: '来源', render: (r) => (
      <span className="muted">{r.legacy_erpnext_id ? 'ERPNext' : '系统内建'}</span>
    )},
  ];

  return (
    <>
      {editor ? (
        <PriceListEditor
          initial={editor.mode === 'edit' ? editor.row : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.name);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<PriceListRow>
        title="价目表"
        desc="价目表及价格明细 · 币种、单位、适用客户、有效期，供新订单按规则选用。历史导入保留原价，不被当前价覆盖。"
        endpoint="/api/price-lists"
        columns={columns}
        rowId={(r) => r.id}
        searchPlaceholder="搜索价目表名称…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        actions={canCreate ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '＋ 新建价目表'}
          </button>
        ) : undefined}
        rowActions={canEdit ? (row) => (
          <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <button className="btn link" onClick={() => setEditor({ mode: 'edit', row })} title="编辑价目表">编辑</button>
            <button className="btn link row-del" onClick={() => { setDelErr(null); setDelRow(row); }} title="删除价目表">删除</button>
          </div>
        ) : undefined}
        renderDetail={(row) => <PriceListDetail listId={row.id} />}
      />

      {!canEdit && !canCreate ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。价目表的新建、编辑与删除由销售、财务与管理员执行（服务端强制校验）。
        </div>
      ) : null}

      <ConfirmDialog
        open={!!delRow}
        title="删除价目表"
        message={<>确认删除价目表「{delRow?.name}」及其全部价格明细？该操作不可恢复，并写入审计日志。</>}
        detail={delErr ? <span style={{ color: 'var(--bad)' }}>{delErr}</span> : undefined}
        tone="danger"
        confirmLabel="确认删除"
        busy={delBusy}
        onConfirm={remove}
        onCancel={() => setDelRow(null)}
      />
    </>
  );
}

function PriceListDetail({ listId }: { listId: string }) {
  const [items, setItems] = useState<PriceListItem[] | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useState(() => {
    let alive = true;
    (async () => {
      try {
        const { api } = await import('../../lib/api');
        const res = await api.get<{ data: PriceListItem[] }>(`/api/price-lists/${listId}/items?limit=500`);
        if (!alive) return;
        setItems(res.data || []);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : '加载失败');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  });

  if (loading) return <div style={{ padding: 12 }}>加载价格明细…</div>;
  if (error) return <div className="notice bad" style={{ margin: 12 }}>{error}</div>;
  if (!items || !items.length) return <div className="muted" style={{ padding: 12 }}>暂无价格明细</div>;

  return (
    <div style={{ padding: '8px 4px', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={th}>物料</th>
            <th style={th}>名称</th>
            <th style={th}>单价</th>
            <th style={th}>币种</th>
            <th style={th}>单位</th>
            <th style={th}>适用客户</th>
            <th style={th}>有效期</th>
            <th style={th}>起订量</th>
            <th style={th}>来源ID</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={td}><code>{it.item_code || '—'}</code></td>
              <td style={td}>{it.item_name || '—'}</td>
              <td style={td} className="num">{money(it.unit_price)}</td>
              <td style={td}>{it.currency || '—'}</td>
              <td style={td}>{it.uom || '—'}</td>
              <td style={td}>{it.customer_code || <span className="muted">通用</span>}</td>
              <td style={td} className="num">
                {it.valid_from ? dateLabel(it.valid_from) : '—'}
                {it.valid_until ? ` ~ ${dateLabel(it.valid_until)}` : ''}
              </td>
              <td style={td} className="num">{num(it.min_quantity, 0)}</td>
              <td style={td} className="muted">{it.legacy_erpnext_id || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>
        共 {items.length} 条 · 历史导入保留原价（币种/单位/客户/有效期），新订单可选用
      </div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontWeight: 600 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };
